import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ReactFlowProvider } from "@xyflow/react";
import { Button, Breadcrumb, Form, Switch } from "antd";
import { FaArrowLeft, FaPlus } from "react-icons/fa";
import { BiMessageSquareEdit, BiMinus, BiPlus } from "react-icons/bi";
import Swal from "sweetalert2";
import { v4 as uuid } from "uuid";

import LiveDashboardCanvas from "@/components/live-dashboard/canvas/LiveDashboardCanvas";
import { resetLiveDashboard, triggerSave, setShowComponentSelect } from "@/redux/slices/liveDashboard.slice";
import { popFromHistory } from "@/redux/slices/dashboardV2.slice";
import { setActiveDashboard } from "@/redux/slices/user.slice";
import { useGetByIdDashboardQuery, useUpdateDashboardMutation } from "@/redux/api/dashboardApi";
import { useSmoothTabZoom } from "@/hooks/useSmoothTabZoom";
import DashboardTabs from "./tabs/DashboardTabs";
import TimePeriodFilter from "./TimePeriodFilter";
import MetaDashboardModal from "./MetaDashboardModal";
import appConfig from "@/config/appConfig";

import "@/styles/liveDashboard.css";

const LiveDashboardEditor = () => {
  const { id: dashboardId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { dashboardName, isDirty } = useSelector((state) => state.liveDashboard);
  const { prevDashboardHistory } = useSelector((state) => state.dashboardV2) || {};

  const [isEditable, setIsEditable] = useState(false);
  const [updateDashboardModal, setUpdateDashboardModal] = useState(false);
  const [data, setData] = useState({});
  const [form] = Form.useForm();
  const [formData, setFormData] = useState({ comparisonPeriods: [{ color: "red" }] });
  const [tabId, setTabId] = useState("");

  const { tabZoom, targetTabZoom, handleTabZoomChange, handleResetTabZoom } = useSmoothTabZoom(1);

  const renderTabZoomControls = () => {
    if (!data?.hasTabs) return null;
    return (
      <div className="dashboard-tab-zoom-controls-theme">
        <span className="px-2 small fw-medium" style={{ color: "var(--color-black)", fontSize: "12px", whiteSpace: "nowrap" }}>
          Tabs Zoom:
        </span>
        <button
          type="button"
          className="zoom-btn"
          onClick={() => handleTabZoomChange(-0.1)}
          disabled={targetTabZoom <= 0.5}
          title="Zoom Out Tabs"
          aria-label="Zoom Out Tabs"
        >
          <BiMinus size={14} />
        </button>
        <span
          className="zoom-badge"
          onClick={handleResetTabZoom}
          title="Click to reset zoom"
        >
          {Math.round(targetTabZoom * 100)}%
        </span>
        <button
          type="button"
          className="zoom-btn"
          onClick={() => handleTabZoomChange(0.1)}
          disabled={targetTabZoom >= 1.6}
          title="Zoom In Tabs"
          aria-label="Zoom In Tabs"
        >
          <BiPlus size={14} />
        </button>
      </div>
    );
  };


  const { data: dashboardData, refetch: refetchDashboard } = useGetByIdDashboardQuery({ id: dashboardId }, { skip: !dashboardId });
  const [updateDashboard] = useUpdateDashboardMutation();

  useEffect(() => {
    if (dashboardData?.data) {
      let dData = dashboardData.data;
      const targetTabId = new URLSearchParams(location.search).get("tabId");
      if (dData?.hasTabs) {
        if (!dData?.tabs || dData.tabs.length === 0) {
          dData.tabs = [
            {
              name: "Tab 1",
              tabId: uuid(),
              isSelected: true,
              liveCanvas: dData.liveCanvas || {},
            },
          ];
        } else {
          // If tab 0 has no liveCanvas or empty nodes, but root has liveCanvas nodes, migrate to tab 0
          const tab0 = dData.tabs[0];
          const hasTab0Nodes = tab0.liveCanvas?.nodes && tab0.liveCanvas.nodes.length > 0;
          if (!hasTab0Nodes && dData.liveCanvas?.nodes?.length > 0) {
            dData.tabs[0] = {
              ...tab0,
              liveCanvas: dData.liveCanvas,
            };
          }

          const hasMatch = targetTabId && dData.tabs.some((t) => t.tabId === targetTabId || t._id === targetTabId);
          const matchIdx = hasMatch ? dData.tabs.findIndex((t) => t.tabId === targetTabId || t._id === targetTabId) : 0;
          dData = {
            ...dData,
            tabs: dData.tabs.map((tab, idx) => ({
              ...tab,
              isSelected: idx === matchIdx,
            })),
          };
        }
        const activeTab = dData?.tabs?.find((t) => t?.isSelected) || dData?.tabs?.[0];
        setTabId(activeTab?._id || activeTab?.tabId || "");
      } else {
        if ((!dData.liveCanvas?.nodes || dData.liveCanvas.nodes.length === 0) && dData.tabs?.[0]?.liveCanvas?.nodes?.length > 0) {
          dData.liveCanvas = dData.tabs[0].liveCanvas;
        }
        setTabId("");
      }
      setData(dData);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardData, location.search]);

  // Page title
  useEffect(() => {
    document.title = `${appConfig?.name || "IVIS"} - Live Dashboard`;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(resetLiveDashboard());
    };
  }, [dispatch]);

  // Warn user before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  const toggleHandler = (checked) => {
    if (checked) {
      setIsEditable(true);
    } else {
      Swal.fire({
        title: "Save Changes?",
        text: "By clicking confirm, the changes will be saved. Click cancel to abandon any edits.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        confirmButtonText: "Confirm",
      }).then(async (result) => {
        if (result.isConfirmed) {
          if (data?.hasTabs && data?.tabs?.length > 0) {
            try {
              await updateDashboard({
                id: dashboardId,
                body: {
                  hasTabs: true,
                  tabs: data.tabs,
                },
              }).unwrap();
            } catch (err) {
              // eslint-disable-next-line no-console
              console.log(err);
            }
          } else {
            dispatch(triggerSave());
          }
          setIsEditable(false);
        } else {
          window.location.reload();
        }
      });
    }
  };

  const customSelectorProps = useMemo(
    () => ({
      form,
      gridData: data?.hasTabs ? data.tabs?.find((t) => t.isSelected)?.items : data?.tabs?.[0]?.items,
      formData,
      setFormData,
      dashboard: data,
      tabId,
      data,
      setData,
    }),
    [form, data, formData, tabId]
  );

  return (
    <div className="live-dashboard-editor">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <label className="az-content-label">Live Dashboard</label>
          <Breadcrumb
            className="mt-1"
            items={[
              {
                title: (
                  <a onClick={() => navigate("/dashboard-list")} style={{ cursor: "pointer" }}>
                    Dashboards
                  </a>
                ),
              },
              { title: dashboardName || "Live Dashboard" },
            ]}
          />
        </div>

        <div className="d-flex align-items-center gap-2">
          {isEditable && (
            <>
              <Button
                className="fw-medium bg_light_gray"
                type="primary"
                icon={<BiMessageSquareEdit />}
                onClick={() => setUpdateDashboardModal(true)}
              >
                Edit Dashboard
              </Button>
              <Button
                type="primary"
                className="fw-medium bg_light_gray"
                icon={<FaPlus />}
                onClick={() => dispatch(setShowComponentSelect(true))}
              >
                Add Items
              </Button>
            </>
          )}
          {prevDashboardHistory && prevDashboardHistory.length > 0 ? (
            <Button
              type="default"
              icon={<FaArrowLeft />}
              onClick={() => {
                const prev = prevDashboardHistory[prevDashboardHistory.length - 1];
                dispatch(popFromHistory());
                if (typeof prev === "object" && prev !== null) {
                  if (prev.activeDashboardId) {
                    dispatch(setActiveDashboard(prev.activeDashboardId));
                  }
                  let targetUrl = prev.url || "/";
                  if (prev.activeTabId && !targetUrl.includes("tabId=")) {
                    targetUrl += (targetUrl.includes("?") ? "&" : "?") + `tabId=${prev.activeTabId}`;
                  }
                  navigate(targetUrl);
                } else if (typeof prev === "string") {
                  navigate(prev);
                }
              }}
            >
              Back
            </Button>
          ) : (
            <Button
              type="default"
              icon={<FaArrowLeft />}
              onClick={() => navigate("/dashboard-list")}
            >
              Back to List
            </Button>
          )}
          {renderTabZoomControls()}
          <Switch checked={isEditable} onChange={toggleHandler} />
        </div>
      </div>

      <div className="d-block d-sm-flex pb-3">
        <TimePeriodFilter customSelectorProps={customSelectorProps} />
      </div>

      <hr className="mt-0 mb-3" />

      {data?.hasTabs && data?.tabs?.length > 0 && (
        <div className="mb-3">
          <DashboardTabs
            {...{
              mode: !isEditable ? "view" : "create",
              data,
              setData,
              dashboard: data,
              setTabId,
              tabZoom,
            }}
          />
        </div>
      )}

      {/* Canvas – wrapped in ReactFlowProvider */}
      <ReactFlowProvider key={`${dashboardId}-${tabId || "default"}`}>
        <LiveDashboardCanvas
          key={`${dashboardId}-${tabId || "default"}`}
          dashboardId={dashboardId}
          tabId={tabId}
          tab={data?.tabs?.find((t) => t.isSelected) || data?.tabs?.[0]}
          setData={setData}
          readOnly={!isEditable}
        />
      </ReactFlowProvider>

      <div className="relative" style={{ zIndex: 999999 }}>
        {updateDashboardModal && (
          <MetaDashboardModal
            {...{
              show: updateDashboardModal,
              setShow: setUpdateDashboardModal,
              mode: "edit",
              data,
              setData,
              getDashboard: () => {
                refetchDashboard();
              },
            }}
          />
        )}
      </div>
    </div>
  );
};

export default LiveDashboardEditor;
